import { Account } from "@common/types/domains/accounting/account";
import { Ledger } from "@common/contracts/ledger.contract";
import { displayAccountLabel } from "@common/helpers/accounting";
import { isUncategorizedAccount } from "@common/helpers/accounting/uncategorized-account";
import { getScheduleFieldLock } from "@common/helpers/schedule-transaction-field-lock";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import {
    LedgerCellVariantSchema,
    ReviewColumnMetaSchema,
} from "@common/types/views/transactionViews";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import {
    useIsReadonlyOrLocked,
    useIsTableReadonly,
} from "@hooks/useIsReadonlyOrLocked";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useTransactionLedgerAccount } from "@hooks/useTransactionLedgerAccount";
import { getTransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { useOverlays } from "contexts/OverlayContext";
import { addTransactionFieldReviews } from "@api/transaction.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUser } from "@hooks/useUser";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLedgers } from "@api/ledger.api";
import { useCloseAndBlur } from "@hooks/useCloseAndBlur";
import {
    buildLedgerOverride,
    readOverrideAccountId,
} from "@lib/accountOverrides";
import {
    getLedgerBasisFromColumnId,
    getLedgerNameFromColumnId,
} from "@lib/ledgerViewColumns";
import classNames from "classnames";
import { getMatchedDocumentFieldLock } from "@common/helpers/matched-document-transaction-fields";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { TransferMatchLink } from "../TransferMatchLink/TransferMatchLink";
import {
    TransactionPreviewData,
    toTransactionPreviewData,
} from "../TransactionPreview/TransactionPreview";
import { updateTransactionInCache } from "../useTransactionsQuery";
import { TransactionFieldReviewButton } from "./TransactionFieldReviewButton";
import { getLedgerMatchLink } from "./categoryMatchIcon";
import {
    isTransactionFieldReviewedByUser,
    mergeTransactionFieldReviews,
} from "./transactionFieldReviewUtils";
import styles from "./LedgerCell.module.scss";

type LedgerCellProps = CellContext<
    ViewTableFeatures,
    TransactionListItem,
    unknown
> & {
    basis?: AccountingLedgerBasis;
    ledgerId?: string;
    ledgerName?: string;
};

export const LedgerCell = memo(
    ({
        row,
        table,
        column,
        basis = AccountingLedgerBasis.cash,
        ledgerId,
        ledgerName,
    }: LedgerCellProps) => {
        const transaction = row.original;
        const showAccountNumbers = useShowAccountNumbers();
        const user = useUser();
        const { error: errorToast } = useToaster();
        const { saveTransaction } = useContext(
            PaginatedTransactionsActionsContext,
        );
        const { openOverlay } = useOverlays();
        const { enabled: isGlFirst, initializing: isGlFirstInitializing } =
            useIsGlFirst();

        // Resolve the review variant from the column's `customVariants` meta.
        const rawMeta = ReviewColumnMetaSchema.safeParse(column.columnDef.meta);
        const rawVariant =
            rawMeta.data?.customVariants?.variant ?? rawMeta.data?.variant;
        const variantParse = LedgerCellVariantSchema.safeParse(rawVariant);
        const isReviewVariant =
            variantParse.success && variantParse.data === "review";

        const isReadonlyOrLocked = useIsReadonlyOrLocked(row.original, table);
        const isTableReadonly = useIsTableReadonly(table);
        const { isLocked: isMatchLocked, tooltip: matchLockTooltip } =
            getMatchedDocumentFieldLock({
                matchLists: [
                    transaction.transactionMatches,
                    transaction.accrualMatches,
                ],
                isOtherwiseLocked: isReadonlyOrLocked,
            });
        const { registerCellActionOpener } = getTransactionsTableMeta(table);
        const [isOpen, setIsOpen] = useState(false);
        const cellRef = useRef<HTMLDivElement>(null);
        const closeCombobox = useCallback(() => {
            setIsOpen(false);
        }, []);
        const closeAndBlur = useCloseAndBlur(closeCombobox);

        const columnLedgerBasis = getLedgerBasisFromColumnId(column.id);
        const columnLedgerName = getLedgerNameFromColumnId(column.id);
        const effectiveBasis = columnLedgerBasis ?? basis;
        const effectiveLedgerName = ledgerName ?? columnLedgerName;

        const entityIdForGLAccount = useEntityIdForGLAccount(transaction);

        const { data: ledgers } = useLedgers(entityIdForGLAccount);
        const ledger = useMemo(() => {
            if (ledgerId) {
                return (ledgers ?? []).find((l) => l.id === ledgerId);
            }
            if (effectiveLedgerName) {
                return (ledgers ?? []).find(
                    (l) =>
                        l.basis === effectiveBasis &&
                        l.name === effectiveLedgerName,
                );
            }
            return (ledgers ?? []).find((l) => l.basis === effectiveBasis);
        }, [ledgers, ledgerId, effectiveLedgerName, effectiveBasis]);
        const ledgerBasis = ledger?.basis ?? effectiveBasis;
        const isAccrualBasis = ledgerBasis === AccountingLedgerBasis.accruals;
        const { isLocked: isScheduleLocked, tooltip: scheduleLockTooltip } =
            getScheduleFieldLock({
                schedule: isAccrualBasis ? transaction.schedule : undefined,
                ledgerId: ledger?.id,
            });
        // The period lock wins and shows no tooltip; a match lock wins over
        // the schedule lock.
        const lockTooltip = isReadonlyOrLocked
            ? undefined
            : (matchLockTooltip ?? scheduleLockTooltip);
        const isDisabled =
            isReadonlyOrLocked || isMatchLocked || isScheduleLocked;

        // Review field is derived from the resolved ledger basis so the
        // "reviewed" state and auto-review write target the correct field
        // for both cash and accrual ledger columns.
        const reviewField = isAccrualBasis
            ? TransactionReviewField.ACCRUAL_LEDGER
            : TransactionReviewField.CASH_LEDGER;

        // Whether the current user has already reviewed this ledger field.
        const isReviewed = useMemo(
            () =>
                isTransactionFieldReviewedByUser(
                    transaction.fieldReviews,
                    reviewField,
                    user?.id,
                ),
            [transaction.fieldReviews, user?.id, reviewField],
        );

        const ledgerOverrideAccountId = readOverrideAccountId(
            transaction.accountOverrides,
            ledger?.id,
        );

        const currentAccountId = getCurrentAccountId({
            transaction,
            ledger,
            isExplicitLedgerColumn: !!ledgerId || !!effectiveLedgerName,
            ledgerOverrideAccountId,
            isAccrualBasis,
        });
        const hasExplicitOverride = hasAccountOverride({
            transaction,
            isExplicitLedgerColumn: !!ledgerId || !!effectiveLedgerName,
            ledgerOverrideAccountId,
            isAccrualBasis,
        });

        const { account, disableAccountSelect } = useTransactionLedgerAccount(
            transaction,
            {
                includeCurrentAccount: true,
                currentAccountId,
                basis: ledgerBasis,
                ledgerId: ledger?.id,
            },
        );

        // A blank ledger (no account resolved) or an account resolving to one
        // of the "Uncategorized Income/Expenses" placeholders is never
        // eligible for review — mirrors the backend's eligibility check so
        // the mark-reviewed affordance never appears for either state.
        const isAccountReviewIneligible =
            !account ||
            isUncategorizedAccount({
                role: account.role ?? null,
                kickOriginalCode: account.kickOriginalCode,
            });

        const handleUpdate = useCallback(
            async (selectedAccount: Account | null) => {
                if (isDisabled) {
                    return;
                }

                const updateData = {
                    accountOverrides: buildLedgerOverride(
                        ledger?.id,
                        selectedAccount?.id ?? null,
                    ),
                };

                await saveTransaction(
                    row.original,
                    updateData,
                    selectedAccount
                        ? displayAccountLabel(
                              selectedAccount,
                              showAccountNumbers,
                          )
                        : undefined,
                );
                // In review variant, auto-mark the ledger field as reviewed
                // after a successful account change — but never when the
                // newly selected account is one of the "Uncategorized
                // Income/Expenses" placeholders (mirrors the backend's
                // eligibility check).
                if (
                    isReviewVariant &&
                    selectedAccount &&
                    !isUncategorizedAccount({
                        role: selectedAccount.role ?? null,
                        kickOriginalCode: selectedAccount.kickOriginalCode,
                    })
                ) {
                    try {
                        const reviews = await addTransactionFieldReviews(
                            row.original.id,
                            [reviewField],
                        );
                        updateTransactionInCache(row.original.id, (t) => ({
                            ...t,
                            fieldReviews: mergeTransactionFieldReviews(
                                t.fieldReviews,
                                reviews,
                            ),
                        }));
                    } catch {
                        errorToast(
                            "Failed to mark ledger as reviewed. Please try again.",
                        );
                    }
                }
                closeAndBlur();
            },
            [
                saveTransaction,
                row.original,
                isDisabled,
                ledger,
                showAccountNumbers,
                closeAndBlur,
                isReviewVariant,
                errorToast,
                reviewField,
            ],
        );

        const columnId = column.id;

        const scrollCellIntoHorizontalView = useCallback(() => {
            cellRef.current?.scrollIntoView({
                inline: "center",
                block: "center",
            });
        }, []);

        useEffect(() => {
            if (!registerCellActionOpener) {
                return;
            }

            return registerCellActionOpener({
                columnId,
                rowId: row.original.id,
                open: () => {
                    if (!isDisabled && !disableAccountSelect) {
                        scrollCellIntoHorizontalView();
                        setIsOpen(true);
                    }
                },
            });
        }, [
            registerCellActionOpener,
            columnId,
            row.original.id,
            isDisabled,
            disableAccountSelect,
            scrollCellIntoHorizontalView,
        ]);

        const matchPreview: TransactionPreviewData | null = useMemo(
            () => toTransactionPreviewData(transaction.pairedTransfer),
            [transaction.pairedTransfer],
        );

        if (!ledger || !entityIdForGLAccount) {
            return null;
        }

        // GL-first workspaces have no Category column, so the matched-transfer
        // indicator is surfaced in the Cash ledger column instead. This shows
        // UI on a true GL-first state, so we wait until the workspace has
        // loaded (`!isGlFirstInitializing`) to avoid flashing the icon on
        // non-GL-first workspaces during initialization.
        const matchLink = getLedgerMatchLink({
            transaction,
            isGlFirst,
            isGlFirstInitializing,
            isReviewVariant,
            isAccrualBasis,
        });
        const reviewTooltip = isAccrualBasis
            ? "Review accrual ledger"
            : "Review cash ledger";

        return (
            <ViewTableCell captureClicks className={styles.ledgerCell}>
                <div
                    ref={cellRef}
                    className={classNames({
                        [styles.accountWrapper]: matchLink.isShown,
                    })}
                >
                    <AccountCombobox
                        showAccountCode={showAccountNumbers}
                        selectedAccount={account ?? undefined}
                        onChange={handleUpdate}
                        entityId={entityIdForGLAccount}
                        canRevertToDefault={hasExplicitOverride}
                        readOnly={isDisabled || disableAccountSelect}
                        disabled={isDisabled || disableAccountSelect}
                        ledgerBasis={ledgerBasis}
                        open={isOpen}
                        onOpenChange={setIsOpen}
                        tooltip={
                            disableAccountSelect
                                ? "Select any business category to modify Cash ledger"
                                : lockTooltip
                        }
                        showDisabledAdd={!!lockTooltip}
                        triggerClassName={classNames({
                            [styles.reviewVariant]: isReviewVariant,
                            [styles.isReviewed]: isReviewed,
                        })}
                    />
                </div>

                {isReviewVariant && (
                    <TransactionFieldReviewButton
                        transaction={transaction}
                        isReadonly={isTableReadonly}
                        field={reviewField}
                        reviewTooltip={reviewTooltip}
                        disableMarking={isAccountReviewIneligible}
                    />
                )}

                {matchLink.isShown && (
                    <TransferMatchLink
                        onClick={() =>
                            openOverlay("transactionMatch", {
                                transactionId: transaction.id,
                            })
                        }
                        transaction={transaction}
                        matchPreview={matchPreview}
                        documentMatches={matchLink.documentMatches}
                    />
                )}
            </ViewTableCell>
        );
    },
);
LedgerCell.displayName = "LedgerCell";

interface GetCurrentAccountIdParams {
    transaction: TransactionListItem;
    ledger?: Ledger;
    isExplicitLedgerColumn: boolean;
    ledgerOverrideAccountId: string | null;
    isAccrualBasis: boolean;
}

function getCurrentAccountId({
    transaction,
    ledger,
    isExplicitLedgerColumn,
    ledgerOverrideAccountId,
    isAccrualBasis,
}: GetCurrentAccountIdParams): string | undefined {
    if (!isExplicitLedgerColumn || !ledger) {
        return getPrimaryAccountId(transaction, isAccrualBasis) ?? undefined;
    }

    const primaryAccountId = ledger.isPrimary
        ? getPrimaryAccountId(transaction, isAccrualBasis)
        : null;
    return ledgerOverrideAccountId ?? primaryAccountId ?? undefined;
}

interface HasAccountOverrideParams {
    transaction: TransactionListItem;
    isExplicitLedgerColumn: boolean;
    ledgerOverrideAccountId: string | null;
    isAccrualBasis: boolean;
}

function hasAccountOverride({
    transaction,
    isExplicitLedgerColumn,
    ledgerOverrideAccountId,
    isAccrualBasis,
}: HasAccountOverrideParams): boolean {
    if (isExplicitLedgerColumn) {
        return ledgerOverrideAccountId != null;
    }

    return getPrimaryAccountId(transaction, isAccrualBasis) != null;
}

function getPrimaryAccountId(
    transaction: TransactionListItem,
    isAccrualBasis: boolean,
): string | null | undefined {
    return isAccrualBasis
        ? transaction.accrualAccountId
        : transaction.accountId;
}
